import type { ActivityEntry } from '../../store/stream'

export type TimelineSegment = {
  id: string
  kind: 'thinking' | 'content' | 'tool' | 'card'
  text?: string
  name?: string
  args?: unknown
  result?: unknown
  success?: boolean
  error?: string
  finished: boolean
  /** For `card` segments: the review resource the card renders. */
  resourceId?: string
  title?: string
}

/**
 * Keep only the current turn's events for `threadId`: scope to the thread,
 * then slice from the last `message_complete` / `agent_error` — the activity
 * log persists across turns, so everything before that boundary belongs to
 * completed turns.
 */
export function sliceCurrentTurn(
  activity: ActivityEntry[],
  threadId: string,
): ActivityEntry[] {
  const threaded = activity.filter((e) => e.data.thread_id === threadId)
  let start = 0
  for (let i = threaded.length - 1; i >= 0; i--) {
    const t = threaded[i].type
    if (t === 'message_complete' || t === 'agent_error') {
      start = i + 1
      break
    }
  }
  return threaded.slice(start)
}

/**
 * Reduce the agent's (topic-scoped) activity log to an ordered timeline for
 * the in-flight turn in this thread. Three concerns:
 *
 *  - Scope: keep only this thread's entries (`data.thread_id === threadId`);
 *    the agent runs one thread at a time but the log accumulates across
 *    threads, so this keeps main-channel / sibling-thread activity out.
 *  - Boundary: slice from the last `message_complete` / `agent_error` so only
 *    the current (not-yet-completed) turn's events remain — the log persists
 *    across turns.
 *  - Interleaving + pairing: thinking / content / tool segments appear in the
 *    order they were emitted, and each `tool_start` is paired with its
 *    matching `tool_finish`.
 *
 * Sub-agent-sourced chunks (`data.source_agent_id` set) are skipped: they're
 * attributed to the parent elsewhere and would duplicate the primary's stream.
 *
 * Text coalescing: consecutive thinking (or content) chunks that have nothing
 * *rendered* between them form ONE segment. The log interleaves entries that
 * never render in this timeline — sub-agent-sourced chunks, `tool_progress`,
 * other threads' events — and each would otherwise split a single model
 * thinking block into several adjacent fragments. Anything that does render
 * (a tool row, content between thinking, a resource card) is a real boundary:
 * one segment per LLM round-trip, matching the historical view.
 */
export function buildTimeline(
  activity: ActivityEntry[],
  threadId: string,
): TimelineSegment[] {
  const turnEvents = sliceCurrentTurn(activity, threadId)
  const segs: TimelineSegment[] = []
  for (const e of turnEvents) {
    if (e.data.source_agent_id) continue
    if (e.type === 'thinking_chunk') {
      const text = String(e.data.text ?? '')
      if (!text) continue
      const last = segs[segs.length - 1]
      if (last?.kind === 'thinking') {
        last.text = (last.text ?? '') + text
      } else {
        segs.push({
          id: `think-${segs.length}`,
          kind: 'thinking',
          text,
          finished: false,
        })
      }
    } else if (e.type === 'content_chunk') {
      const text = String(e.data.text ?? '')
      if (!text) continue
      const last = segs[segs.length - 1]
      if (last?.kind === 'content') {
        last.text = (last.text ?? '') + text
      } else {
        segs.push({
          id: `content-${segs.length}`,
          kind: 'content',
          text,
          finished: false,
        })
      }
    } else if (e.type === 'tool_start') {
      segs.push({
        id: `tool-${segs.length}`,
        kind: 'tool',
        name: String(e.data.name ?? ''),
        args: e.data.args,
        finished: false,
      })
    } else if (e.type === 'tool_finish') {
      const name = String(e.data.name ?? '')
      const finish = {
        result: e.data.result,
        success: e.data.success !== false,
        error: e.data.error ? String(e.data.error) : undefined,
      }
      // Pair with the most recent unfinished tool segment of the same name.
      // Tool calls fan out concurrently, so start/finish may interleave;
      // name + "unfinished" resolves the common case unambiguously.
      const open = [...segs]
        .reverse()
        .find((s) => s.kind === 'tool' && s.name === name && !s.finished)
      if (open) {
        open.finished = true
        open.success = finish.success
        open.result = finish.result
        open.error = finish.error
      } else {
        segs.push({
          id: `tool-${segs.length}`,
          kind: 'tool',
          name,
          ...finish,
          finished: true,
        })
      }
    } else if (e.type === 'resource_card') {
      // A blocking review (plan/question) became interactive mid-turn: the
      // card lands below the running tool call it belongs to. Deduped by
      // resource id — the WS replay buffer can re-deliver the ready event.
      const rid = String(e.data.resource_id ?? '')
      if (rid && !segs.some((s) => s.kind === 'card' && s.resourceId === rid)) {
        segs.push({
          id: `card-${segs.length}`,
          kind: 'card',
          resourceId: rid,
          title: e.data.title ? String(e.data.title) : undefined,
          finished: true,
        })
      }
    }
  }
  return segs
}
